import React, { useState, useEffect, useCallback } from "react";
import { getAuthHeaders } from "../utils/auth";
import { TermsModal, PrivacyPolicyModal, AppFooter } from "./LegalModals";
import { useLanguage } from "../utils/i18n";

const API = "/api";

const PLANS = [
  {
    id: "default",
    name: "Default",
    price: "$19.9",
    period: "/month",
    color: "#34d399",
    features: [
      /*
      "100 voice translations / month",
      "200 AI calls / month",
      "PDF upload & reader",
      "Notes & folders",
      "All payment methods",
      */
    ],
  },
  /*
  {
    id: "basic",
    name: "Basic",
    price: "A$39",
    period: "/month",
    color: "#4f8ef7",
    features: [
      "200 voice translations / month",
      "500 AI calls / month",
      "PDF upload & reader",
      "Notes & folders",
      "All payment methods",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: "A$69",
    period: "/month",
    color: "#ff69b4",
    badge: "Most Popular",
    features: [
      "1,000 voice translations / month",
      "3,000 AI calls / month",
      "PDF upload & reader",
      "Notes & folders",
      "Priority support",
      "All payment methods",
    ],
  },
  */
];

export default function PaywallPage({ billingStatus, onRefresh, onBack }) {
  const { t } = useLanguage();
  const [loading, setLoading]         = useState(null); // which plan card is loading (card)
  const [onetimeLoading, setOnetimeLoading] = useState(null); // which plan is loading (alipay/wechat)
  const [portalLoading, setPortalLoading]   = useState(false);
  const [syncLoading, setSyncLoading]       = useState(false);
  const [syncMsg, setSyncMsg]               = useState("");
  const [error, setError]             = useState("");
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const isExpired    = billingStatus?.plan === "expired";
  const isTrial      = billingStatus?.plan === "free_trial";
  const isPaid       = billingStatus?.plan === "default" || billingStatus?.plan === "basic" || billingStatus?.plan === "pro";

  const handleSubscribe = useCallback(async (planId) => {
    setError("");
    setLoading(planId);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API}/billing/checkout`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ plan: planId }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Checkout failed.");
        return;
      }
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(null);
    }
  }, []);

  const handlePayOnetime = useCallback(async (planId) => {
    setError("");
    setOnetimeLoading(planId);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API}/billing/checkout-onetime`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ plan: planId }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Checkout failed.");
        return;
      }
      window.location.href = data.url;
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setOnetimeLoading(null);
    }
  }, []);

  const handleSync = useCallback(async () => {
    setSyncMsg("");
    setError("");
    setSyncLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API}/billing/sync`, { method: "POST", headers });
      const data = await res.json();
      if (!res.ok) {
        setSyncMsg(data.error || "同步失败，请稍后重试。");
        return;
      }
      if (data.synced) {
        setSyncMsg("✅ 付款已确认！正在更新...");
        setTimeout(() => { onRefresh && onRefresh(); }, 800);
      } else {
        setSyncMsg("未检测到有效付款记录。如有疑问请联系客服。");
      }
    } catch {
      setSyncMsg("网络错误，请重试。");
    } finally {
      setSyncLoading(false);
    }
  }, [onRefresh]);

  const handlePortal = useCallback(async () => {
    setError("");
    setPortalLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API}/billing/portal`, {
        method: "POST",
        headers,
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Could not open billing portal.");
        return;
      }
      window.location.href = data.url;
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  }, []);

  // Parse trial_end date nicely
  const trialEndFormatted = React.useMemo(() => {
    if (!billingStatus?.trial_end) return null;
    try {
      return new Date(billingStatus.trial_end).toLocaleDateString(undefined, {
        year: "numeric", month: "long", day: "numeric",
      });
    } catch {
      return billingStatus.trial_end;
    }
  }, [billingStatus]);

  const usagePct = (used, limit) => limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;

  return (
    <div style={styles.overlay}>
      {onBack && (
        <button onClick={onBack} style={styles.backBtn}>
          {t("backBtn")}
        </button>
      )}
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}><span style={{ color: "#1d4ed8" }}>NOTE</span><span style={{ color: "#3b82f6" }}>MILA</span></h1>
        </div>

        {/* Status banner */}
        {isExpired && (
          <div style={{ ...styles.banner, background: "rgba(239,68,68,0.18)", border: "1px solid rgba(239,68,68,0.4)" }}>
            <span style={{ fontSize: 20 }}>⏰</span>
            <div>
              <div style={{ fontWeight: 700, color: "#f87171" }}>{t("trialEndedStatus")}</div>
              <div style={{ fontSize: 13, color: "#cbd5e1", marginTop: 2 }}>
                {t("trialEndedHint")}
              </div>
            </div>
          </div>
        )}
        {isTrial && (
          <div style={{ ...styles.banner, background: "rgba(79,142,247,0.15)", border: "1px solid rgba(79,142,247,0.35)" }}>
            <span style={{ fontSize: 20 }}>🎉</span>
            <div>
              <div style={{ fontWeight: 700, color: "#93c5fd" }}>{t("trialActiveExpires")} {trialEndFormatted}</div>
              <div style={{ fontSize: 13, color: "#cbd5e1", marginTop: 2 }}>
                {t("trialActiveHint")}
              </div>
            </div>
          </div>
        )}
        {isPaid && (
          <div style={{ ...styles.banner, background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.35)" }}>
            <span style={{ fontSize: 20 }}>✅</span>
            <div>
              <div style={{ fontWeight: 700, color: "#86efac" }}>
                {t("paidStatusPrefix")} {billingStatus.plan_name} {t("paidStatusSuffix")}
              </div>
              <div style={{ fontSize: 13, color: "#cbd5e1", marginTop: 2 }}>
                {t("paidHint")}
              </div>
            </div>
          </div>
        )}

        {/* Usage meters — temporarily hidden */}

        {/* Sync payment status — subtle text link, no longer a big banner button */}

        {/* Plan cards */}
        {(!isPaid || isExpired) && (
          <>
            <h2 style={styles.plansTitle}>
              {isExpired ? t("reactivateAccount") : t("choosePlan")}
            </h2>
            <div style={styles.plansRow}>
              {PLANS.map((plan) => (
                <div key={plan.id} style={{ ...styles.planCard, borderColor: plan.color + "55" }}>
                  {plan.badge && (
                    <div style={{ ...styles.badge, background: plan.color }}>
                      {plan.badge}
                    </div>
                  )}
                  <div style={{ ...styles.planName, color: plan.color }}>{plan.name}</div>
                  <div style={styles.planPrice}>
                    <span style={styles.planPriceNum}>{plan.price}</span>
                    <span style={styles.planPricePeriod}>{plan.period}</span>
                  </div>
                  <ul style={styles.featureList}>
                    {plan.features.map((f) => (
                      <li key={f} style={styles.featureItem}>
                        <span style={{ color: plan.color, marginRight: 8 }}>✓</span>{f}
                      </li>
                    ))}
                  </ul>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 12 }}>
                    Visa / Mastercard — recurring monthly
                  </div>
                  <button
                    style={{ ...styles.subscribeBtn, background: plan.color, opacity: loading === plan.id ? 0.7 : 1 }}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={!!loading || !!onetimeLoading}
                  >
                    {loading === plan.id ? t("redirectingLabel") : `${t("subscribeBtn")} — ${plan.price}/mo`}
                  </button>
                  <div style={{ fontSize: 12, color: "#94a3b8", margin: "10px 0 6px", textAlign: "center" }}>
                    {t("orPayOnce")}
                  </div>
                  <button
                    style={{
                      ...styles.subscribeBtn,
                      background: "transparent",
                      border: `1.5px solid ${plan.color}88`,
                      color: "#e2e8f0",
                      fontSize: 14,
                      opacity: onetimeLoading === plan.id ? 0.7 : 1,
                    }}
                    onClick={() => handlePayOnetime(plan.id)}
                    disabled={!!loading || !!onetimeLoading}
                  >
                    {onetimeLoading === plan.id
                      ? t("redirectingLabel")
                      : ` Alipay / WeChat — ${plan.price} (30 days)`}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Manage subscription for paid users */}
        {isPaid && (
          <div style={styles.manageBox}>
            <h2 style={{ ...styles.plansTitle, marginTop: 0 }}>{t("manageSubscriptionTitle")}</h2>
            <p style={{ color: "#94a3b8", fontSize: 14, marginBottom: 16 }}>
              {t("manageSubHint")}
            </p>
            <button
              style={{ ...styles.subscribeBtn, background: "#334155", maxWidth: 280 }}
              onClick={handlePortal}
              disabled={portalLoading}
            >
              {portalLoading ? t("openingLabel") : t("openBillingPortal")}
            </button>
          </div>
        )}

        {/* Sync button — small subtle link below the plan cards */}
        <div style={{ marginTop: 24, textAlign: "center" }}>
          <button
            onClick={handleSync}
            disabled={syncLoading}
            style={{
              background: "none",
              border: "1px solid #475569",
              color: syncLoading ? "#64748b" : "#cbd5e1",
              fontSize: 14,
              cursor: syncLoading ? "default" : "pointer",
              textDecoration: "none",
              padding: "8px 20px",
              borderRadius: 8,
              borderStyle: "dashed",
            }}
          >
            {syncLoading ? "🔄 检查中..." : "已付款？点击刷新状态"}
          </button>
          {syncMsg && (
            <div style={{
              marginTop: 8,
              fontSize: 13,
              color: syncMsg.startsWith("✅") ? "#86efac" : "#fca5a5",
            }}>
              {syncMsg}
            </div>
          )}
        </div>

        {/* Manage subscription for paid users */}
      </div>
      <AppFooter onShowTerms={() => setShowTerms(true)} onShowPrivacy={() => setShowPrivacy(true)} />
      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyPolicyModal onClose={() => setShowPrivacy(false)} />}
    </div>
  );
}

const styles = {
  overlay: {
    minHeight: "100vh",
    background: "#0f1624",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "40px 16px 0",
    overflowY: "auto",
    position: "relative",
  },
  container: {
    width: "100%",
    maxWidth: 780,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: 1,
    paddingBottom: 40,
  },
  backBtn: {
    position: "absolute",
    top: 20,
    left: 20,
    zIndex: 10,
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: 10,
    padding: "8px 16px",
    fontSize: 13,
    color: "#cbd5e1",
    cursor: "pointer",
    fontWeight: 600,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 28,
  },
  title: {
    fontSize: 32,
    fontWeight: 700,
    fontFamily: "'Orbitron', sans-serif",
    color: "#fff",
    margin: 0,
    letterSpacing: "0.13em",
  },
  banner: {
    width: "100%",
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    borderRadius: 12,
    padding: "14px 18px",
    marginBottom: 20,
    boxSizing: "border-box",
  },
  usageBox: {
    width: "100%",
    background: "rgba(255,255,255,0.04)",
    borderRadius: 12,
    padding: "18px 22px",
    marginBottom: 28,
    boxSizing: "border-box",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  usageTitle: {
    fontWeight: 700,
    color: "#e2e8f0",
    marginBottom: 14,
    fontSize: 14,
  },
  usageRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  usageLabel: { color: "#94a3b8", fontSize: 13 },
  usageCount: { color: "#e2e8f0", fontSize: 13, fontWeight: 600 },
  progressBar: {
    height: 6,
    background: "rgba(255,255,255,0.08)",
    borderRadius: 3,
    overflow: "hidden",
    marginBottom: 4,
  },
  progressFill: {
    height: "100%",
    borderRadius: 3,
    transition: "width 0.4s",
  },
  plansTitle: {
    color: "#fff",
    fontWeight: 700,
    fontSize: 22,
    marginBottom: 20,
    textAlign: "center",
  },
  plansRow: {
    display: "flex",
    gap: 20,
    width: "100%",
    flexWrap: "wrap",
    justifyContent: "center",
    marginBottom: 20,
  },
  planCard: {
    flex: "1 1 300px",
    maxWidth: 360,
    background: "rgba(255,255,255,0.04)",
    border: "1.5px solid",
    borderRadius: 16,
    padding: "24px 24px 20px",
    position: "relative",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },
  badge: {
    position: "absolute",
    top: -12,
    left: "50%",
    transform: "translateX(-50%)",
    padding: "3px 16px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    color: "#fff",
    letterSpacing: 0.5,
    whiteSpace: "nowrap",
  },
  planName: {
    fontSize: 20,
    fontWeight: 800,
    marginBottom: 6,
  },
  planPrice: {
    display: "flex",
    alignItems: "baseline",
    gap: 4,
    marginBottom: 18,
  },
  planPriceNum: { fontSize: 34, fontWeight: 800, color: "#fff" },
  planPricePeriod: { fontSize: 14, color: "#94a3b8" },
  featureList: {
    listStyle: "none",
    padding: 0,
    margin: "0 0 16px",
    flex: 1,
  },
  featureItem: {
    color: "#cbd5e1",
    fontSize: 13,
    padding: "5px 0",
    display: "flex",
    alignItems: "flex-start",
  },
  subscribeBtn: {
    width: "100%",
    padding: "12px 0",
    borderRadius: 10,
    border: "none",
    color: "#fff",
    fontWeight: 700,
    fontSize: 15,
    cursor: "pointer",
    transition: "opacity 0.2s",
  },
  syncBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: 24,
    width: "100%",
  },
  manageBox: {
    width: "100%",
    background: "rgba(255,255,255,0.04)",
    borderRadius: 16,
    padding: "24px",
    boxSizing: "border-box",
    border: "1px solid rgba(255,255,255,0.08)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: 20,
  },
  errorBox: {
    background: "rgba(239,68,68,0.15)",
    border: "1px solid rgba(239,68,68,0.4)",
    borderRadius: 10,
    padding: "12px 18px",
    color: "#f87171",
    fontSize: 14,
    marginBottom: 16,
    width: "100%",
    boxSizing: "border-box",
    textAlign: "center",
  },
  refreshBtn: {
    background: "none",
    border: "none",
    color: "#64748b",
    fontSize: 13,
    cursor: "pointer",
    marginTop: 8,
    textDecoration: "underline",
  },
};
